// public/js/user/userViews.js
import BaseViews from '@base/baseViews.js';
import userManager from './userManager.js';

class UserViews extends BaseViews {
    constructor() {
        // Call parent constructor with user-specific configuration AND manager for schema access
        super({
            entityName: 'user',
            entityNamePlural: 'users',
            manager: userManager // Pass manager for schema-driven functionality
        });
        
        // Extend iconMap with user-specific icons after super() call
        this.iconMap = {
            ...this.iconMap,
            // User-specific icons
            person: 'person',
            star: 'star',
            profile: 'account_circle',
            settings: 'settings',
            email: 'email',
            verified: 'verified',
            pending: 'pending',
            lock_reset: 'lock_reset',
            // Gender icons
            male: 'man',
            female: 'woman',
            other: 'transgender',
            gender: 'wc'
        };

        this.logger.info('UserViews initialized with schema-driven BaseViews');
    }

    // ===============================
    // PROFILE IMAGE SECTION (for Edit Form)
    // ===============================

    /**
     * Create profile image section (expandable)
     * @param {Object} profileImageData - Profile image section data
     * @returns {HTMLElement} Profile image section element
     */
    createProfileImageSection(profileImageData) {
        this.logger.debug('Creating profile image section', profileImageData);

        const section = document.createElement('div');
        section.className = 'profile-image-section collapsed';
        section.id = `profile-image-section-${profileImageData.userId}`;

        // Header (always visible, clickable to expand/collapse)
        const header = document.createElement('div');
        header.className = 'profile-image-section-header';
        header.innerHTML = `
            <span class="material-symbols-outlined section-icon">photo_camera</span>
            <h3 class="section-title">Profile Image</h3>
            <span class="material-symbols-outlined toggle-icon">expand_more</span>
        `;

        // Toggle expansion on header click
        header.addEventListener('click', () => {
            section.classList.toggle('collapsed');
            section.classList.toggle('expanded');
            const toggleIcon = header.querySelector('.toggle-icon');
            toggleIcon.textContent = section.classList.contains('collapsed') ? 'expand_more' : 'expand_less';
        });

        section.appendChild(header);

        // Content (hidden when collapsed)
        const content = document.createElement('div');
        content.className = 'profile-image-section-content';

        // Upload new image subsection
        const uploadSubsection = document.createElement('div');
        uploadSubsection.className = 'profile-image-upload-subsection';

        const uploadTitle = document.createElement('h4');
        uploadTitle.textContent = 'Upload New Image';
        uploadTitle.className = 'subsection-title';
        uploadSubsection.appendChild(uploadTitle);

        if (profileImageData.uploadField) {
            uploadSubsection.appendChild(profileImageData.uploadField);
        }

        content.appendChild(uploadSubsection);

        // Previous images gallery subsection (only if there are existing images)
        if (profileImageData.existingImages && profileImageData.existingImages.length > 0) {
            const gallerySubsection = document.createElement('div');
            gallerySubsection.className = 'profile-image-gallery-subsection';

            const galleryTitle = document.createElement('h4');
            galleryTitle.textContent = 'Select from Previous Images';
            galleryTitle.className = 'subsection-title';
            gallerySubsection.appendChild(galleryTitle);

            const gallery = document.createElement('div');
            gallery.className = 'profile-image-gallery';

            profileImageData.existingImages.forEach(image => {
                const thumbnail = this.createProfileImageThumbnail(image, profileImageData);
                gallery.appendChild(thumbnail);
            });

            gallerySubsection.appendChild(gallery);
            content.appendChild(gallerySubsection);
        }

        section.appendChild(content);

        // Add hidden input to store selected profile image ID
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.id = 'user-selected-profile-image-id';
        hiddenInput.name = 'selected_profile_image_id';
        hiddenInput.value = profileImageData.currentProfileImage?.id || '';
        section.appendChild(hiddenInput);

        return section;
    }

    /**
     * Create individual thumbnail for profile image gallery
     * @param {Object} image - Image object
     * @param {Object} profileImageData - Profile image section data
     * @returns {HTMLElement} Thumbnail element
     */
    createProfileImageThumbnail(image, profileImageData) {
        const thumbnail = document.createElement('div');
        thumbnail.className = 'profile-image-thumbnail';
        thumbnail.dataset.fileId = image.id;

        // Check if this is the current profile image
        const isCurrent = profileImageData.currentProfileImage?.id === image.id;
        if (isCurrent) {
            thumbnail.classList.add('current');
        }

        const img = document.createElement('img');
        img.src = image.url || image.src || '/images/user.png';
        img.alt = image.file_name || 'User profile image';
        img.className = 'thumbnail-image';

        thumbnail.appendChild(img);

        // Current indicator
        if (isCurrent) {
            const indicator = document.createElement('div');
            indicator.className = 'current-indicator';
            indicator.innerHTML = '<span class="material-symbols-outlined">check_circle</span>';
            thumbnail.appendChild(indicator);
        }

        // Click to set as profile image
        thumbnail.addEventListener('click', async () => {
            await this.handleProfileImageSelect(image, profileImageData.userId);
        });

        return thumbnail;
    }

    /**
     * Handle profile image selection from gallery
     * @param {Object} image - Selected image
     * @param {string} userId - User ID
     */
    async handleProfileImageSelect(image, userId) {
        this.logger.debug(`Selecting profile image ${image.id} for user ${userId}`);

        try {
            // Update all thumbnails to remove current indicator
            const thumbnails = document.querySelectorAll('.profile-image-thumbnail');
            thumbnails.forEach(thumb => {
                thumb.classList.remove('current');
                const indicator = thumb.querySelector('.current-indicator');
                if (indicator) indicator.remove();
            });

            // Add current indicator to selected thumbnail
            const selectedThumbnail = document.querySelector(`.profile-image-thumbnail[data-file-id="${image.id}"]`);
            if (selectedThumbnail) {
                selectedThumbnail.classList.add('current');
                const indicator = document.createElement('div');
                indicator.className = 'current-indicator';
                indicator.innerHTML = '<span class="material-symbols-outlined">check_circle</span>';
                selectedThumbnail.appendChild(indicator);
            }

            // Store selected image ID in hidden input for form submission
            const hiddenInput = document.getElementById('user-selected-profile-image-id');
            if (hiddenInput) {
                hiddenInput.value = image.id;
                this.logger.debug(`Stored selected profile image ID: ${image.id}`);
            }

            this.logger.success('Profile image selection updated in UI');
        } catch (error) {
            this.logger.error('Error selecting profile image:', error);
        }
    }

    /**
     * Override createEntityEditView to inject profile image section
     * PATTERN: Prepare → Super → Post-process
     */
    createEntityEditView(formData) {
        // 1. PREPARE: Extract profile image section data
        const profileImageSection = formData.profileImageSection;

        // 2. SUPER: Generate base edit view from BaseViews
        const baseEditView = super.createEntityEditView(formData);

        // 3. POST-PROCESS: Insert profile image section at top (after title)
        if (profileImageSection) {
            const imageSection = this.createProfileImageSection(profileImageSection);
            const title = baseEditView.querySelector('h2');

            if (title) {
                title.insertAdjacentElement('afterend', imageSection);
            } else {
                // Fallback: insert at beginning
                baseEditView.insertBefore(imageSection, baseEditView.firstChild);
            }

            this.logger.ui('Profile image section added to user edit form');
        }

        return baseEditView;
    }

    // ===============================
    // USER PROFILE IMPLEMENTATION
    // ===============================

    /**
     * Override createEntityDetails for user-specific details view (User Profile)
     * PATTERN: Standard schema-driven override
     * @param {Object} detailsData - Prepared details data from UserManager
     * @returns {HTMLElement} User details element
     */
    createEntityDetails(detailsData) {
        const userDetails = document.createElement('div');
        userDetails.id = `user-details-${detailsData.id}`;
        userDetails.className = 'user-details entity-details';

        // Header
        const header = this.createUserDetailsHeader(detailsData);
        userDetails.appendChild(header);

        // Body with sections
        const body = this.createDetailsBody(detailsData.body);
        userDetails.appendChild(body);

        // Actions (Edit Profile button)
        if (detailsData.isCurrentUser) {
            const actions = this.createUserDetailsActions(detailsData.actions || []);
            userDetails.appendChild(actions);
        }

        return userDetails;
    }
   
    /**
     * Create user details header
     * @param {Object} detailsData - Details data
     * @returns {HTMLElement} Header element
     */
    createUserDetailsHeader(detailsData) {
        const header = document.createElement('div');
        header.className = 'user-details-header entity-details-header';

        // User avatar/icon
        const avatarContainer = document.createElement('div');
        avatarContainer.className = 'user-avatar-container';
        
        const avatar = document.createElement('div');
        avatar.className = 'user-avatar';
        
        // Use actual image, gender-based placeholder, or default icon
        if (detailsData.header && detailsData.header.imageUrl) {
            const img = document.createElement('img');
            img.src = detailsData.header.imageUrl;
            img.className = detailsData.header.useGenderFallback ? 'user-avatar-placeholder' : 'user-avatar-image';
            img.alt = detailsData.header.altText || 'Profile Image';
            
            // Add error handler for gender-based placeholders
            if (detailsData.header.useGenderFallback) {
                img.onerror = () => {
                    // If gender-based image fails to load, show Material icon
                    avatar.innerHTML = `<span class="material-symbols-outlined">account_circle</span>`;
                };
            }
            
            avatar.appendChild(img);
        } else {
            // Default Material icon
            avatar.innerHTML = `<span class="material-symbols-outlined">account_circle</span>`;
        }
        
        avatarContainer.appendChild(avatar);
        header.appendChild(avatarContainer);

        return header;
    }



    /**
     * Create user image editor view
     * @param {HTMLElement} fileField - File field element
     * @returns {HTMLElement} Image editor view
     */
    createUserImageEditor(fileField) {
        const userImageView = document.createElement('div');
        userImageView.className = 'user-image-view entity-edit-view';

        const imageTitle = document.createElement('h2');
        imageTitle.textContent = 'Change Your Profile Image';

        const closeButton = document.createElement('button');
        closeButton.textContent = 'Close';
        closeButton.className = 'close-button';
        closeButton.id = 'user-image-close';

        userImageView.appendChild(imageTitle);
        userImageView.appendChild(fileField);
        userImageView.appendChild(closeButton);

        return userImageView;
    }

    /**
     * Create user details actions
     * @param {Array} actions - Array of action configurations
     * @returns {HTMLElement} Actions element
     */
    createUserDetailsActions(actions) {
        const actionsSection = document.createElement('div');
        actionsSection.className = 'user-details-actions entity-details-actions';

        // Default edit profile action if none provided
        if (actions.length === 0) {
            actions = [
                { name: 'edit', icon: 'edit', title: 'Edit Profile', entityId: 'current' }
            ];
        }

        actions.forEach(action => {
            const button = this.createUserActionButton(action, true);
            actionsSection.appendChild(button);
        });

        return actionsSection;
    }

    /**
     * Create user-specific action button
     * @param {Object} action - Action configuration
     * @param {boolean} includeText - Whether to include text
     * @returns {HTMLElement} Button element
     */
    createUserActionButton(action, includeText = false) {
        const button = document.createElement('button');
        button.className = `action-button ${action.name}`;
        button.setAttribute('data-action', action.name);
        button.setAttribute('data-user-id', action.entityId);
        button.setAttribute('title', action.title || action.name);

        const icon = document.createElement('span');
        icon.className = 'material-symbols-outlined';
        icon.textContent = action.icon || this.iconMap[action.name] || 'circle';
        button.appendChild(icon);

        if (includeText) {
            const text = document.createElement('span');
            text.className = 'button-text';
            text.textContent = action.title || this.formatFieldName(action.name);
            button.appendChild(text);
        }

        return button;
    }

    /**
     * Override createEntityEditView for user-specific forms
     * @param {Object} formData - Prepared form data from UserManager
     * @returns {HTMLElement} Edit view element
     */
    createUserEditView(formData) {
        const container = document.createElement('div');
        container.className = 'user-edit-view entity-edit-view';

        const title = document.createElement('h2');
        title.textContent = formData.title || 'Edit Profile';
        container.appendChild(title);

        const form = document.createElement('div');
        form.className = 'user-edit-content entity-edit-content';
        form.classList.add('user-form'); // For form validation

        // Generate form fields from schema - only editable ones
        const editableFields = formData.fields.filter(field => 
            ['firstName', 'lastName', 'gender', 'email'].includes(field.name)
        );
        
        editableFields.forEach(field => {
            const fieldElement = this.createFormField(field);
            form.appendChild(fieldElement);
        });

        // Buttons
        const buttonContainer = this.createFormButtons(formData.buttons);
        form.appendChild(buttonContainer);

        container.appendChild(form);

        return container;
    }

    /**
     * Override createFormField to handle gender select field
     * @param {Object} field - Field configuration
     * @returns {HTMLElement} Form field element
     */
    createFormField(field) {
        // Handle gender field specifically
        if (field.name === 'gender') {
            return this.createGenderSelectField(field);
        }
        
        // Use base implementation for other fields
        return super.createFormField(field);
    }

    /**
     * Create gender select field
     * @param {Object} field - Field configuration
     * @returns {HTMLElement} Gender select field element
     */
    createGenderSelectField(field) {
        const fieldContainer = document.createElement('div');
        fieldContainer.className = 'form-field form-field-select';

        // Label
        const label = document.createElement('label');
        label.htmlFor = field.name;
        label.textContent = field.label || 'Gender';
        if (field.required) {
            label.innerHTML += ' <span class="required">*</span>';
        }
        fieldContainer.appendChild(label);

        // Select element
        const select = document.createElement('select');
        select.id = field.name;
        select.name = field.name;
        select.className = 'form-control';
        
        if (field.required) {
            select.required = true;
        }

        // Add options
        const genderOptions = [
            { value: 'prefer_not_to_say', text: 'Keine Angabe' },
            { value: 'male', text: 'Männlich' },
            { value: 'female', text: 'Weiblich' },
            { value: 'other', text: 'Andere' }
        ];

        genderOptions.forEach(option => {
            const optionElement = document.createElement('option');
            optionElement.value = option.value;
            optionElement.textContent = option.text;
            
            // Set selected if this matches the current value
            if (field.value === option.value) {
                optionElement.selected = true;
            }
            
            select.appendChild(optionElement);
        });

        fieldContainer.appendChild(select);

        // Help text
        if (field.helpText) {
            const helpText = document.createElement('div');
            helpText.className = 'form-help-text';
            helpText.textContent = field.helpText;
            fieldContainer.appendChild(helpText);
        }

        return fieldContainer;
    }

    /**
     * Create gender display element for details view
     * @param {string} gender - Gender value
     * @returns {HTMLElement} Gender display element
     */
    createGenderDisplay(gender) {
        const genderContainer = document.createElement('div');
        genderContainer.className = 'gender-display';

        const icon = document.createElement('span');
        icon.className = 'material-symbols-outlined gender-icon';
        
        // Set icon based on gender
        switch (gender) {
            case 'male':
                icon.textContent = 'man';
                break;
            case 'female':
                icon.textContent = 'woman';
                break;
            case 'other':
                icon.textContent = 'transgender';
                break;
            default:
                icon.textContent = 'wc';
                break;
        }

        const text = document.createElement('span');
        text.className = 'gender-text';
        text.textContent = userManager.getGenderDisplayName({ gender });

        genderContainer.appendChild(icon);
        genderContainer.appendChild(text);

        return genderContainer;
    }

    // ===============================
    // PLACEHOLDER METHODS (Return Empty Divs)
    // ===============================

    /**
     * Create user container - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserContainer() {
        const container = document.createElement('div');
        container.id = 'user-container';
        container.className = 'user-container entity-container';
        container.innerHTML = '<p>User container - not yet implemented</p>';
        return container;
    }

    /**
     * Create user card - placeholder
     * @param {Object} cardData - Card data
     * @returns {HTMLElement} Empty div
     */
    createUserCard(cardData) {
        const card = document.createElement('div');
        card.className = 'user-card entity-card placeholder';
        card.innerHTML = `
            <div class="placeholder-content">
                <h3>${cardData.body?.title || 'User Card'}</h3>
                <p>User card implementation - coming soon</p>
            </div>
        `;
        return card;
    }

    /**
     * Create user cards container - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserCardsContainer() {
        const container = document.createElement('div');
        container.id = 'user-cards-container';
        container.className = 'user-cards-container entity-cards-container';
        container.innerHTML = '<p>User cards container - not yet implemented</p>';
        return container;
    }

    /**
     * Create new user card - placeholder
     * @returns {HTMLElement} Empty div
     */
    createNewUserCard() {
        const card = document.createElement('div');
        card.className = 'user-card-new entity-card-new';
        card.innerHTML = '<p>New user card - not applicable</p>';
        return card;
    }

    /**
     * Create user list view - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserListView() {
        const listView = document.createElement('div');
        listView.className = 'user-list-view';
        listView.innerHTML = '<p>User list view - not yet implemented</p>';
        return listView;
    }

    /**
     * Create user search view - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserSearchView() {
        const searchView = document.createElement('div');
        searchView.className = 'user-search-view';
        searchView.innerHTML = '<p>User search view - not yet implemented</p>';
        return searchView;
    }

    /**
     * Create user mini card - placeholder
     * @param {Object} userData - User data
     * @returns {HTMLElement} Empty div
     */
    createUserMiniCard(userData) {
        const miniCard = document.createElement('div');
        miniCard.className = 'user-mini-card';
        miniCard.innerHTML = `<p>Mini card for ${userData.firstName || 'User'} - not yet implemented</p>`;
        return miniCard;
    }

    /**
     * Display filtered results - placeholder
     * @param {Array} entities - Filtered entities
     * @returns {HTMLElement} Empty div
     */
    displayFilteredResults(entities) {
        const container = document.createElement('div');
        container.className = 'filtered-results';
        container.innerHTML = `<p>Filtered results (${entities.length} users) - not yet implemented</p>`;
        return container;
    }

    /**
     * Create user settings view - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserSettingsView() {
        const settingsView = document.createElement('div');
        settingsView.className = 'user-settings-view';
        settingsView.innerHTML = '<p>User settings view - not yet implemented</p>';
        return settingsView;
    }

    /**
     * Create user activity view - placeholder
     * @returns {HTMLElement} Empty div
     */
    createUserActivityView() {
        const activityView = document.createElement('div');
        activityView.className = 'user-activity-view';
        activityView.innerHTML = '<p>User activity view - not yet implemented</p>';
        return activityView;
    }

    /**
     * Create related users view - placeholder
     * @returns {HTMLElement} Empty div
     */
    createRelatedUsersView() {
        const relatedView = document.createElement('div');
        relatedView.className = 'related-users-view';
        relatedView.innerHTML = '<p>Related users view - not yet implemented</p>';
        return relatedView;
    }

    // ===============================
    // HELPER METHODS FOR USER VIEWS
    // ===============================

    /**
     * Get user role icon
     * @param {string} role - User role
     * @returns {string} Material icon name
     */
    getUserRoleIcon(role) {
        const icons = {
            'user': 'person',
            'pro': 'star',
            'admin': 'admin_panel_settings'
        };
        return icons[role] || icons.user;
    }

    /**
     * Get gender icon
     * @param {string} gender - User gender
     * @returns {string} Material icon name
     */
    getGenderIcon(gender) {
        const icons = {
            'male': 'man',
            'female': 'woman',
            'other': 'transgender',
            'prefer_not_to_say': 'wc'
        };
        return icons[gender] || icons.prefer_not_to_say;
    }

    /**
     * Get user status badge class
     * @param {boolean} isActive - Whether user is active
     * @returns {string} CSS class for badge
     */
    getUserStatusBadgeClass(isActive) {
        return isActive ? 'status-badge active' : 'status-badge inactive';
    }

    /**
     * Format user display name
     * @param {Object} user - User object
     * @returns {string} Formatted display name
     */
    formatUserDisplayName(user) {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        } else if (user.firstName) {
            return user.firstName;
        } else if (user.lastName) {
            return user.lastName;
        } else {
            return user.email?.split('@')[0] || 'User';
        }
    }

    /**
     * Create error state for user views
     * @param {Error} error - Error object
     * @returns {HTMLElement} Error state element
     */
    createUserErrorState(error) {
        const errorState = document.createElement('div');
        errorState.className = 'user-error entity-error';
        errorState.innerHTML = `
            <div class="error-icon">
                <span class="material-symbols-outlined">error</span>
            </div>
            <h3>Failed to Load User Data</h3>
            <p>There was an error loading the user information. Please check your connection and try again.</p>
            <p class="error-details">Error: ${error.message}</p>
            <button class="retry-button">
                Retry Loading
            </button>
        `;
        return errorState;
    }

    /**
     * Create user profile card with gender display
     * @param {Object} user - User data
     * @returns {HTMLElement} Profile card element
     */
    createUserProfileCard(user) {
        const card = document.createElement('div');
        card.className = 'user-profile-card';

        const header = document.createElement('div');
        header.className = 'profile-card-header';
        
        const avatar = document.createElement('div');
        avatar.className = 'profile-avatar';
        avatar.innerHTML = `<span class="material-symbols-outlined">account_circle</span>`;
        
        header.appendChild(avatar);
        card.appendChild(header);

        const body = document.createElement('div');
        body.className = 'profile-card-body';

        const name = document.createElement('h3');
        name.textContent = this.formatUserDisplayName(user);
        body.appendChild(name);

        const email = document.createElement('p');
        email.className = 'profile-email';
        email.textContent = user.email;
        body.appendChild(email);

        // Add gender display if specified
        if (user.gender && user.gender !== 'prefer_not_to_say') {
            const genderDisplay = this.createGenderDisplay(user.gender);
            body.appendChild(genderDisplay);
        }

        card.appendChild(body);

        return card;
    }
}

// Create and export instance
const userViews = new UserViews();
export default userViews;